import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    TextField,
    Box,
    Typography,
    Alert,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    FormControlLabel,
    Switch,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid
} from '@mui/material';

// project imports
import { addFunction, setDeploymentStatus } from 'store/slices/lambdaFunctions';

// icons
import {
    IconCheck,
    IconX,
    IconLoader2,
    IconCode,
    IconSettings,
    IconRocket,
    IconFunction,
    IconFileCode
} from '@tabler/icons-react';

// Sample notebook functions for simulation
const sampleNotebookFunctions = [
    {
        name: 'process_image',
        description: 'Process and resize uploaded images',
        parameters: [
            { name: 'image_url', type: 'str', required: true },
            { name: 'width', type: 'int', required: true },
            { name: 'height', type: 'int', required: true },
            { name: 'format', type: 'str', required: false }
        ]
    },
    {
        name: 'send_notification',
        description: 'Send email or SMS notifications',
        parameters: [
            { name: 'recipient', type: 'str', required: true },
            { name: 'message', type: 'str', required: true },
            { name: 'type', type: 'str', required: true }
        ]
    },
    {
        name: 'analyze_text',
        description: 'Perform sentiment analysis on text',
        parameters: [
            { name: 'text', type: 'str', required: true },
            { name: 'language', type: 'str', required: false }
        ]
    }
];

// ==============================|| DEPLOYMENT MODAL ||============================== //

const DeploymentModal = ({ open, onClose }) => {
    const theme = useTheme();
    const dispatch = useDispatch();

    const [activeStep, setActiveStep] = useState(0);
    const [notebookPath, setNotebookPath] = useState('');
    const [selectedFunctions, setSelectedFunctions] = useState([]);
    const [deployConfig, setDeployConfig] = useState({
        autoScale: true,
        memory: '256Mi',
        timeout: 30,
        rateLimit: '1000/hour',
        environment: 'production'
    });
    const [deploying, setDeploying] = useState(false);
    const [deploymentResults, setDeploymentResults] = useState([]);
    const [parsedFunctions, setParsedFunctions] = useState([]);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleReset = () => {
        setActiveStep(0);
        setNotebookPath('');
        setSelectedFunctions([]);
        setDeploymentResults([]);
        setParsedFunctions([]);
        setDeploying(false);
    };

    const handleClose = () => {
        handleReset();
        onClose();
    };

    const handleParseNotebook = () => {
        // Simulate parsing notebook
        setDeploying(true);
        setTimeout(() => {
            setParsedFunctions(sampleNotebookFunctions);
            setSelectedFunctions(sampleNotebookFunctions.map((f) => f.name));
            setDeploying(false);
            handleNext();
        }, 1500);
    };

    const handleFunctionToggle = (functionName) => {
        setSelectedFunctions((prev) => {
            if (prev.includes(functionName)) {
                return prev.filter((f) => f !== functionName);
            }
            return [...prev, functionName];
        });
    };

    const handleDeploy = () => {
        setDeploying(true);
        dispatch(setDeploymentStatus('deploying'));

        // Simulate deployment process
        const results = [];
        selectedFunctions.forEach((funcName, index) => {
            const func = parsedFunctions.find((f) => f.name === funcName);
            setTimeout(
                () => {
                    const success = Math.random() > 0.1; // 90% success rate
                    const result = {
                        name: funcName,
                        status: success ? 'deployed' : 'failed',
                        message: success ? 'Function deployed successfully' : 'Deployment failed: Syntax error'
                    };
                    results.push(result);
                    setDeploymentResults([...results]);

                    if (success) {
                        dispatch(
                            addFunction({
                                name: funcName,
                                description: func.description,
                                parameters: func.parameters,
                                version: '1.0.0',
                                config: deployConfig,
                                created_by: 'current.user@company.com'
                            })
                        );
                    }

                    if (index === selectedFunctions.length - 1) {
                        setDeploying(false);
                        dispatch(setDeploymentStatus('completed'));
                        handleNext();
                    }
                },
                (index + 1) * 1000
            );
        });
    };

    const steps = [
        {
            label: 'Select Notebook',
            icon: <IconFileCode size={20} />
        },
        {
            label: 'Choose Functions',
            icon: <IconFunction size={20} />
        },
        {
            label: 'Configure',
            icon: <IconSettings size={20} />
        },
        {
            label: 'Deploy',
            icon: <IconRocket size={20} />
        }
    ];

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Box>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            Select a Jupyter notebook containing functions with @deploy decorators
                        </Typography>
                        <TextField
                            fullWidth
                            label="Notebook Path"
                            variant="outlined"
                            value={notebookPath}
                            onChange={(e) => setNotebookPath(e.target.value)}
                            placeholder="/workspace/notebooks/my_functions.ipynb"
                            sx={{ mt: 2, mb: 2 }}
                            helperText="Enter the full path to your Jupyter notebook"
                        />
                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                                Make sure your functions are decorated with <code>@deploy</code> to be discoverable.
                            </Typography>
                        </Alert>
                    </Box>
                );

            case 1:
                return (
                    <Box>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            Select which functions to deploy from the notebook
                        </Typography>
                        <List sx={{ mt: 2 }}>
                            {parsedFunctions.map((func) => (
                                <ListItem
                                    key={func.name}
                                    sx={{
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 1,
                                        mb: 1,
                                        '&:hover': {
                                            backgroundColor: theme.palette.action.hover
                                        }
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={selectedFunctions.includes(func.name)}
                                                onChange={() => handleFunctionToggle(func.name)}
                                            />
                                        }
                                        label=""
                                        sx={{ mr: 2 }}
                                    />
                                    <ListItemText
                                        primary={
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                {func.name}
                                            </Typography>
                                        }
                                        secondary={
                                            <>
                                                <Typography variant="body2" color="textSecondary">
                                                    {func.description}
                                                </Typography>
                                                <Box display="flex" gap={1} mt={1}>
                                                    {func.parameters.map((param) => (
                                                        <Chip
                                                            key={param.name}
                                                            label={`${param.name}: ${param.type}`}
                                                            size="small"
                                                            variant="outlined"
                                                            color={param.required ? 'primary' : 'default'}
                                                        />
                                                    ))}
                                                </Box>
                                            </>
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                );

            case 2:
                return (
                    <Box>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            Configure deployment settings for your functions
                        </Typography>
                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Memory</InputLabel>
                                    <Select
                                        value={deployConfig.memory}
                                        label="Memory"
                                        onChange={(e) => setDeployConfig({ ...deployConfig, memory: e.target.value })}
                                    >
                                        <MenuItem value="128Mi">128 MB</MenuItem>
                                        <MenuItem value="256Mi">256 MB</MenuItem>
                                        <MenuItem value="512Mi">512 MB</MenuItem>
                                        <MenuItem value="1Gi">1 GB</MenuItem>
                                        <MenuItem value="2Gi">2 GB</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Timeout (seconds)"
                                    type="number"
                                    value={deployConfig.timeout}
                                    onChange={(e) => setDeployConfig({ ...deployConfig, timeout: parseInt(e.target.value) })}
                                    inputProps={{ min: 1, max: 900 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Rate Limit"
                                    value={deployConfig.rateLimit}
                                    onChange={(e) => setDeployConfig({ ...deployConfig, rateLimit: e.target.value })}
                                    helperText="e.g., 1000/hour, 100/minute"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Environment</InputLabel>
                                    <Select
                                        value={deployConfig.environment}
                                        label="Environment"
                                        onChange={(e) => setDeployConfig({ ...deployConfig, environment: e.target.value })}
                                    >
                                        <MenuItem value="development">Development</MenuItem>
                                        <MenuItem value="staging">Staging</MenuItem>
                                        <MenuItem value="production">Production</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={deployConfig.autoScale}
                                            onChange={(e) => setDeployConfig({ ...deployConfig, autoScale: e.target.checked })}
                                        />
                                    }
                                    label="Enable auto-scaling"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                );

            case 3:
                return (
                    <Box>
                        {deploying ? (
                            <>
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                    Deploying your functions...
                                </Typography>
                                <LinearProgress sx={{ mt: 2, mb: 3 }} />
                            </>
                        ) : (
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                Deployment completed!
                            </Typography>
                        )}

                        <List>
                            {deploymentResults.map((result, index) => (
                                <ListItem key={index}>
                                    <ListItemIcon>
                                        {result.status === 'deployed' ? (
                                            <IconCheck size={20} color={theme.palette.success.main} />
                                        ) : (
                                            <IconX size={20} color={theme.palette.error.main} />
                                        )}
                                    </ListItemIcon>
                                    <ListItemText primary={result.name} secondary={result.message} />
                                    <Chip label={result.status} size="small" color={result.status === 'deployed' ? 'success' : 'error'} />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    minHeight: '600px'
                }
            }}
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={1}>
                        <IconRocket size={24} />
                        <Typography variant="h4">Deploy Lambda Functions</Typography>
                    </Box>
                    <Chip label="Beta" size="small" color="warning" />
                </Box>
            </DialogTitle>

            <DialogContent>
                <Stepper activeStep={activeStep} orientation="vertical">
                    {steps.map((step, index) => (
                        <Step key={step.label}>
                            <StepLabel
                                StepIconComponent={() => (
                                    <Box
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: '50%',
                                            backgroundColor: activeStep >= index ? theme.palette.primary.main : theme.palette.grey[300],
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white'
                                        }}
                                    >
                                        {step.icon}
                                    </Box>
                                )}
                            >
                                {step.label}
                            </StepLabel>
                            <StepContent>
                                <Box sx={{ mb: 2 }}>{renderStepContent(index)}</Box>
                                <Box sx={{ mb: 2 }}>
                                    {activeStep === 0 && (
                                        <Button
                                            variant="contained"
                                            onClick={handleParseNotebook}
                                            disabled={!notebookPath || deploying}
                                            startIcon={deploying ? <IconLoader2 className="animate-spin" /> : <IconCode />}
                                        >
                                            {deploying ? 'Parsing...' : 'Parse Notebook'}
                                        </Button>
                                    )}
                                    {activeStep === 1 && (
                                        <>
                                            <Button
                                                variant="contained"
                                                onClick={handleNext}
                                                disabled={selectedFunctions.length === 0}
                                                sx={{ mr: 1 }}
                                            >
                                                Continue
                                            </Button>
                                            <Button onClick={handleBack}>Back</Button>
                                        </>
                                    )}
                                    {activeStep === 2 && (
                                        <>
                                            <Button
                                                variant="contained"
                                                onClick={handleDeploy}
                                                disabled={deploying}
                                                startIcon={<IconRocket />}
                                                sx={{ mr: 1 }}
                                            >
                                                Deploy Functions
                                            </Button>
                                            <Button onClick={handleBack}>Back</Button>
                                        </>
                                    )}
                                    {activeStep === 3 && !deploying && (
                                        <Button variant="contained" onClick={handleClose}>
                                            Finish
                                        </Button>
                                    )}
                                </Box>
                            </StepContent>
                        </Step>
                    ))}
                </Stepper>
            </DialogContent>
        </Dialog>
    );
};

export default DeploymentModal;
